import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { GrTransaction } from 'react-icons/gr';
import {
  Button,
  Card,
  CardBody,
  CardSubtitle,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Row
} from 'reactstrap';

import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';

import { getStateConversation } from 'utils/fuctions/getStateConversation';
import useAgentsLoggedInQueues from 'hooks/useAgentsLoggedInQueues';
import { zIndex } from 'constants/zIndex';
import { Option, StyleOption } from '../../styled';
import { useSocketConversationChatTs } from 'hooks/useSocketConversationChatTs';

const sortOptionsByLabel = (options) => {
  if (!options?.length) return [];
  return [...options].sort((a, b) => {
    if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
    if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
    return 0;
  });
};

const TransferConversation = ({
  name,
  tooltipTitle = '',
  id,
  conversation,
  isCurrentAgentsLogged
}) => {
  const {
    allQueueOptions,
    allAgentOptions,
    queuesMap,
    agentsMap,
    fetch: fetchAgents
  } = useAgentsLoggedInQueues({ manual: true });

  const { socketChatTS } = useSocketConversationChatTs();

  const { control, handleSubmit, errors, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      fila: null,
      user: null,
      transferToSelf: false
    }
  });

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const [isOpen, setIsOpen] = useState(false);
  const [transferMode, setTransferMode] = useState('QUEUE_FIRST');
  const [showOnlyWithAgents, setShowOnlyWithAgents] = useState(true);

  const attendantCurrent = conversation?.atendente;

  const conversationRef = useRef(conversation);
  const currentUserLogin = useRef(localStorage.getItem('login'));
  const currentUserName = useRef(localStorage.getItem('name'));
  const userState = useRef('DDD não definido');

  const watchedQueue = watch('fila');
  const watchedUser = watch('user');
  const watchedTransferToSelf = watch('transferToSelf');

  const isChatWithCurrentUser =
    conversation?.atendente?.id_postgres === localStorage.getItem('login');

  /**
   * Computed Options: Filas
   */
  const filteredQueueOptions = useMemo(() => {
    let options = allQueueOptions;

    // Transferir para mim mesmo (Prioritário)
    if (watchedTransferToSelf) {
      const myLogin = currentUserLogin.current;
      options = agentsMap[myLogin]?.queueOptions || [];
    }

    // Contexto USER_FIRST com usuário selecionado
    else if (transferMode === 'USER_FIRST' && watchedUser?.value) {
      options = agentsMap[watchedUser.value]?.queueOptions || [];
    }

    // Filtro de disponibilidade (Global)
    if (showOnlyWithAgents) {
      options = options.filter((opt) => {
        const queueData = queuesMap[opt.value];
        // Verifica existência de array de agentes na fila
        return queueData?.agentOptions?.length > 0;
      });
    }

    return sortOptionsByLabel(options);
  }, [
    transferMode,
    watchedUser,
    watchedTransferToSelf,
    agentsMap,
    allQueueOptions,
    showOnlyWithAgents,
    queuesMap
  ]);

  /**
   * Computed Options: Agentes
   */
  const filteredAgentOptions = useMemo(() => {
    let optionsToFilter = allAgentOptions;

    // Contexto: Fila selecionada define os agentes possíveis
    if (transferMode === 'QUEUE_FIRST' && watchedQueue?.value) {
      optionsToFilter = queuesMap[watchedQueue.value]?.agentOptions || [];
    }

    const currentAgentLogin = conversationRef.current?.atendente?.id_postgres;

    // Remove self da lista de transferência (exceto se for "Transferir para mim", tratado no checkbox separado)
    const validAgents = optionsToFilter.filter(
      (agent) => agent.value !== currentAgentLogin
    );

    return sortOptionsByLabel(validAgents);
  }, [transferMode, watchedQueue, queuesMap, allAgentOptions, agentsMap]);

  useEffect(() => {
    if (isOpen) {
      fetchAgents();

      conversationRef.current = conversation;
      userState.current =
        (conversation?.cliente?.telefone &&
          getStateConversation(conversation.cliente?.telefone)) ||
        'DDD não definido';

      setTransferMode('QUEUE_FIRST');
      setShowOnlyWithAgents(true);

      // Reset inicial
      setValue('fila', null);
      setValue('user', null);
      setValue('transferToSelf', false);
    }
  }, [isOpen, conversation, setValue, fetchAgents]);

  // UX: Monitora o Checkbox "Transferir para mim"
  useEffect(() => {
    if (watchedTransferToSelf) setValue('user', null);
  }, [watchedTransferToSelf, setValue]);

  // Função centralizada para troca de modo e reset de formulário
  const handleModeChange = (newMode) => {
    setTransferMode(newMode);

    // Reseta todos os campos para evitar inconsistências nos filtros cruzados
    setValue('fila', null);
    setValue('user', null);
    setValue('transferToSelf', false);
  };

  const handleTransfer = async (data) => {
    try {
      const targetUserLogin = data.transferToSelf
        ? currentUserLogin.current
        : data.user?.value;

      const filaId = data.fila?.value;
      let selectedAgentData = null;

      // Se houver um atendente alvo (seja self ou selecionado), resolvemos os dados do socket
      if (targetUserLogin) {
        if (data.transferToSelf) {
          selectedAgentData = {
            name: currentUserName.current,
            login: currentUserLogin.current
          };
        } else {
          const agentInfo = agentsMap[targetUserLogin];

          if (agentInfo) {
            selectedAgentData = {
              name: agentInfo.name,
              login: agentInfo.login
            };
          } else {
            const fallbackAgent = isCurrentAgentsLogged?.find(
              (a) => a.login === targetUserLogin
            );
            if (fallbackAgent) {
              selectedAgentData = {
                name: fallbackAgent.name,
                login: fallbackAgent.login
              };
            }
          }
        }

        if (!selectedAgentData) {
          throw new Error('Dados do agente não encontrados.');
        }
      }

      const payload = {
        chatId: id,
        socketAgent: null,
        destinationQueueId: filaId,
        destinationAgentLogin: targetUserLogin || undefined,
        isMultipleTransfer: false,
        responsibleForTransferred: {
          name: currentUserName.current,
          login: currentUserLogin.current
        }
      };

      socketChatTS.emit('transfer_chat', payload);

      setIsOpen(false);
    } catch (err) {
      console.error(err);
      const dataErr = err.response?.data;
      toast.error(dataErr?.message || 'Erro ao transferir conversa!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const toggleModal = () => setIsOpen(!isOpen);

  return (
    <>
      <Option title={tooltipTitle}>
        <Button
          onClick={toggleModal}
          color="link"
          size="sm"
          style={StyleOption}
        >
          <GrTransaction size={20} color="#32325d" />
        </Button>
      </Option>

      <Modal
        className="modal-dialog-centered"
        isOpen={isOpen}
        toggle={toggleModal}
        style={{ zIndex: zIndex.ChatDetailsModal }}
      >
        <div className="modal-header">
          <h6 className="modal-title" id="modal-title-default">
            {name}
          </h6>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={toggleModal}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>

        <ModalBody>
          <Card style={{ backgroundColor: '#FFFFFF', marginTop: '-35px' }}>
            <CardBody>
              <CardTitle tag="h4">
                Cliente:{' '}
                {conversationRef.current?.cliente?.nome || 'Não informado'}
              </CardTitle>
              <CardSubtitle tag="h5" className="mb-3 text-muted">
                <Row>
                  <Col md="6" title="CPF do cliente">
                    <i
                      className="fas fa-id-card"
                      style={{ color: '#5bc0de' }}
                    ></i>
                    - {conversationRef.current?.cliente?.cpf || 'Não informado'}
                  </Col>
                  <Col md="6" title="Canal de atendimento">
                    <i
                      className="fas fa-users"
                      style={{ color: '#0275d8' }}
                    ></i>
                    - {conversationRef.current?.canal || 'Não reconhecido'}
                  </Col>
                </Row>
                <Row>
                  <Col md="6" title="Telefone do cliente">
                    <i
                      className="fas fa-phone"
                      style={{ color: '#5cb85c' }}
                    ></i>
                    -{' '}
                    {conversationRef.current?.cliente?.telefone ||
                      'Não informado'}
                  </Col>
                  <Col md="6" title="Estado">
                    <i className="fas fa-city" style={{ color: '#f0ad4e' }}></i>
                    {conversationRef.current?.cliente?.telefone &&
                      ` - Estado : ${userState.current}`}
                  </Col>
                </Row>
                <Row>
                  <Col md="6" title="Email do cliente">
                    <i
                      className="fas fa-envelope-open-text"
                      style={{ color: '#f0ad4e' }}
                    ></i>
                    -{' '}
                    {conversationRef.current?.cliente?.email || 'Não informado'}
                  </Col>
                  <Col md="6" title="Atendente">
                    <i className="fas fa-user" style={{ color: '#f0ad4e' }}></i>
                    {conversationRef.current?.atendente?.name
                      ? ` - ${conversationRef.current?.atendente?.name}`
                      : ' - Não informado'}
                  </Col>
                </Row>
              </CardSubtitle>
            </CardBody>
          </Card>

          <hr style={{ marginTop: '-20px' }} />

          <Form
            className="needs-validation"
            onSubmit={handleSubmit(handleTransfer)}
          >
            <Row className="mb-3">
              <Col md="12">
                <Label style={{ fontWeight: 'bold' }}>Modo de seleção:</Label>
                <div className="d-flex align-items-center">
                  <FormGroup check inline>
                    <Label check style={{ cursor: 'pointer' }}>
                      <Input
                        type="radio"
                        name="transferMode"
                        checked={transferMode === 'QUEUE_FIRST'}
                        onChange={() => handleModeChange('QUEUE_FIRST')}
                      />{' '}
                      Fila primeiro
                    </Label>
                  </FormGroup>
                  <FormGroup check inline>
                    <Label check style={{ cursor: 'pointer' }}>
                      <Input
                        type="radio"
                        name="transferMode"
                        checked={transferMode === 'USER_FIRST'}
                        onChange={() => handleModeChange('USER_FIRST')}
                      />{' '}
                      Atendente primeiro
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </Row>

            {attendantCurrent && !isChatWithCurrentUser && (
              <Row className="mb-3">
                <Col md="12">
                  <FormGroup check>
                    <Input
                      id="transfer-to-myself3"
                      type="checkbox"
                      checked={watchedTransferToSelf}
                      onChange={() =>
                        setValue('transferToSelf', !watchedTransferToSelf)
                      }
                    />
                    <label
                      className="form-control-label"
                      htmlFor="transfer-to-myself3"
                      style={{ cursor: 'pointer' }}
                    >
                      Transferir para si mesmo
                    </label>
                  </FormGroup>
                </Col>
              </Row>
            )}

            <Row>
              <Col md="12">
                {transferMode === 'QUEUE_FIRST' ? (
                  <>
                    {/* FLUXO QUEUE_FIRST: Fila é mandatória antes do Atendente */}
                    <div className="mb-3">
                      <FlexControllerV2
                        type="select"
                        name="fila"
                        control={control}
                        label="Fila"
                        options={filteredQueueOptions}
                        isClearable={false}
                        isSearchable={true}
                        rules={{ required: 'A fila é obrigatória' }}
                        errors={errors}
                        placeholder="Selecione a fila"
                      />
                    </div>

                    {watchedQueue && !watchedTransferToSelf && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="user"
                          control={control}
                          label="Atendente"
                          options={filteredAgentOptions}
                          isClearable={true}
                          isSearchable={true}
                          errors={errors}
                          placeholder="Selecione o atendente"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* FLUXO USER_FIRST: Atendente é mandatório antes da Fila (exceto self) */}
                    {!watchedTransferToSelf && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="user"
                          control={control}
                          label="Atendente"
                          options={filteredAgentOptions}
                          isClearable={true}
                          isSearchable={true}
                          errors={errors}
                          placeholder="Selecione o atendente"
                        />
                      </div>
                    )}

                    {(watchedUser || watchedTransferToSelf) && (
                      <div className="mb-3">
                        <FlexControllerV2
                          type="select"
                          name="fila"
                          control={control}
                          label="Fila"
                          options={filteredQueueOptions}
                          isClearable={false}
                          isSearchable={true}
                          rules={{ required: 'A fila é obrigatória' }}
                          errors={errors}
                          placeholder="Selecione a fila"
                        />
                      </div>
                    )}
                  </>
                )}
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3">
              <Button
                color="danger"
                data-dismiss="modal"
                type="button"
                onClick={toggleModal}
                style={{
                  ...buttonStyle(systemConfiguration.cancelation_button_color),
                  marginRight: '10px'
                }}
              >
                Fechar
              </Button>

              <Button
                color="primary"
                type="submit"
                style={buttonStyle(
                  systemConfiguration.confirmation_button_color
                )}
              >
                {watchedTransferToSelf ? 'Transferir para mim' : 'Transferir'}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default TransferConversation;
